import { useState, useCallback, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useShell } from "@/features/shell/useShell";
import { SHELL_META, type ShellId } from "@/features/shell/types";
import { useTheme } from "@/features/shell/ThemeProvider";
import { filterNavByRole } from "@/features/shell/nav/adminNav";
import { filterNavByFeatureFlags } from "@/features/shell/nav/navFeatureFlags";
import { useShellNav } from "@/features/shell/nav/useShellNav";
import type {
  NavItem,
  NavItemGroup,
  NavItemStatus,
} from "@/features/shell/nav/adminNav";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  Circle,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import { OrgSwitcher } from "@/components/layout/OrgSwitcher";
import { RailTooltip } from "@/components/layout/RailTooltip";
import {
  SIDEBAR_SHORTCUT_ARIA,
  SIDEBAR_SHORTCUT_LABEL,
} from "@/components/layout/useSidebarCollapsed";
import { useSession } from "@/features/auth/useSession";
import { useI18n, type TranslationKey } from "@/lib/i18n";
import { useProjectWorkspaceSummary } from "@/features/ops/api";
import {
  getProjectNavLifecycleStatus,
  PROJECT_NAV_LIFECYCLE_STATUS_LABEL_KEYS,
  type ProjectNavLifecycleStatus,
} from "@/features/ops/lib/project-lifecycle-navigation";

/* ── Brand assets (fallbacks when theme has no logo URLs) ──── */
import sentinelsLogoWhite from "@/assets/brand/sentinels-white-tagline-hub.png";
import sentinelsLogoDark from "@/assets/brand/sentinels-tagline-hub.png";
/* Symbol cropped from the white wordmark: the collapsed sidebar's logo. */
import sentinelsMarkWhite from "@/assets/brand/sentinels-mark-white.png";

// Dark logo available for light-background contexts (login, topbar, etc.)
void sentinelsLogoDark;

// Nav resolution moved into useShellNav() hook (v0.16.10) — supports
// localStorage-driven overrides written from /admin/web-map editor with
// live re-render on save (no page reload required).

/** Shared with the collapse toggle's `aria-controls`. */
const SHELL_SIDEBAR_ID = "shell-sidebar";

/**
 * Peek: hovering the collapsed rail opens it over the page after a short
 * hover-intent delay, and it folds back a moment after the pointer leaves.
 */
const PEEK_OPEN_DELAY_MS = 200;
const PEEK_CLOSE_DELAY_MS = 300;

interface ShellSidebarProps {
  /**
   * Icon-only rail: logo symbol on top, one icon per area with a tooltip.
   * With a toggle, hovering it opens the full sidebar over the page (peek)
   * without taking width from the page.
   */
  collapsed?: boolean;
  /** Renders the collapse/expand toggle. Omitted in the mobile drawer. */
  onToggleCollapsed?: () => void;
}

export function ShellSidebar({
  collapsed = false,
  onToggleCollapsed,
}: ShellSidebarProps) {
  const { activeShell, availableShells, setActiveShell } = useShell();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useI18n();
  const { theme, isCustomDomain } = useTheme();
  const { session } = useSession();
  const securityRole = session?.permissions?.security_role ?? "";
  const effectivePerms = session?.permissions?.effective_permissions ?? [];
  const shellNav = useShellNav(activeShell);
  const flags = { ...session?.feature_flags };
  const rawNav = {
    main: filterNavByFeatureFlags(shellNav.main, flags),
    bottom: filterNavByFeatureFlags(shellNav.bottom, flags),
  };
  const main =
    activeShell === "admin"
      ? filterNavByRole(rawNav.main, securityRole, effectivePerms)
      : rawNav.main;
  const bottom =
    activeShell === "admin"
      ? filterNavByRole(rawNav.bottom, securityRole, effectivePerms)
      : rawNav.bottom;
  const showSwitcher = availableShells.length > 1;
  const hasClientSelector =
    !isCustomDomain &&
    (securityRole === "platform_admin" ||
      (session?.organizations ?? []).some(
        (org) =>
          org.org_type === "customer_group" || org.org_type === "customer",
      ));

  // Resolve logo sources from theme, with static fallbacks. Collapsed, the
  // rail shows a square mark: the theme's favicon, else an organization's own
  // sidebar logo (white-label branding never falls back to Sentinels), else
  // the Sentinels symbol — the default theme's wordmark is too wide for it.
  const sidebarLogo = theme.logos.sidebar ?? sentinelsLogoWhite;
  const customSidebarLogo =
    sidebarLogo !== sentinelsLogoWhite ? sidebarLogo : undefined;
  const sidebarMark =
    theme.logos.favicon ?? customSidebarLogo ?? sentinelsMarkWhite;
  const clientLogoSrc = theme.logos.sidebar_client;
  const appTitle = theme.meta.app_title ?? "Sentinels Hub";

  // Build provenance (#373): Vite inlines __APP_BUILD_SHA__ from VITE_GIT_SHA
  // at image build time so a test session can name the exact deployed commit.
  // The `typeof` guard keeps the badge alive when the define is absent (dev
  // server without the variable, unit tests) instead of throwing.
  const buildSha =
    typeof __APP_BUILD_SHA__ === "string" && __APP_BUILD_SHA__
      ? __APP_BUILD_SHA__
      : "unknown";
  const buildSuffix =
    buildSha === "unknown" ? "build unknown" : buildSha.slice(0, 7);
  const buildLabel = `v${__APP_VERSION__} · ${buildSuffix}`;
  const buildTitle = `v${__APP_VERSION__} · build ${buildSha}`;

  // Switching shell moves the operator, it does not merely repaint the
  // sidebar (#421). The destination is the shell root — the same canonical
  // map `ShellGuard` and `ShellRedirect` navigate to — so each shell's own
  // index resolves the landing (Command's scope-aware index included) instead
  // of a hardcoded child. Selecting the active shell is a no-op, and a shell
  // the operator has no access to is never navigated to: `availableShells` is
  // derived from the same `shellAccess` that makes `setActiveShell` refuse.
  const handleShellSwitch = useCallback(
    (shell: ShellId) => {
      if (shell === activeShell) return;
      if (!availableShells.includes(shell)) return;
      setActiveShell(shell);
      navigate(SHELL_META[shell].basePath, { replace: true });
    },
    [activeShell, availableShells, navigate, setActiveShell],
  );

  // Peek (desktop only: the drawer has no toggle). Mouse and pen only — a
  // tap on a touch screen means "go there", not "show me the labels".
  const [peeking, setPeeking] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [peekPath, setPeekPath] = useState(pathname);
  const pointerInside = useRef(false);
  const peekTimer = useRef<number | undefined>(undefined);
  const canPeek = collapsed && onToggleCollapsed !== undefined;
  const isPeeking = canPeek && peeking;
  /** What the sidebar draws: the rail, unless it is peeking. */
  const rail = collapsed && !isPeeking;

  // A destination was picked, or the page moved on: fold back.
  if (pathname !== peekPath) {
    setPeekPath(pathname);
    setPeeking(false);
  }

  useEffect(() => () => window.clearTimeout(peekTimer.current), []);

  const schedulePeek = useCallback((open: boolean, delay: number) => {
    window.clearTimeout(peekTimer.current);
    peekTimer.current = window.setTimeout(() => setPeeking(open), delay);
  }, []);

  const handlePointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    pointerInside.current = true;
    // Back before the grace period ran out: stay open.
    if (isPeeking) window.clearTimeout(peekTimer.current);
  };

  // Only the logo and the areas open the peek. The footer (client, shell,
  // collapse toggle) works from the rail: peeking there would slide the
  // toggle away from under the pointer just as it goes to click it.
  const handleNavPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    if (canPeek && !isPeeking) schedulePeek(true, PEEK_OPEN_DELAY_MS);
  };

  const handleFooterPointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    if (!isPeeking) window.clearTimeout(peekTimer.current);
  };

  const handlePointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") return;
    pointerInside.current = false;
    // An open client or shell menu lives in a portal outside the sidebar;
    // keep the peek until it closes.
    if (menuOpen) window.clearTimeout(peekTimer.current);
    else schedulePeek(false, PEEK_CLOSE_DELAY_MS);
  };

  const handleMenuOpenChange = useCallback(
    (open: boolean) => {
      setMenuOpen(open);
      if (!open && !pointerInside.current) {
        schedulePeek(false, PEEK_CLOSE_DELAY_MS);
      }
    },
    [schedulePeek],
  );

  // WP 1.4: Logo 404 fallback — revert to static Sentinels logo on error
  const handleLogoError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const fallback = rail ? sentinelsMarkWhite : sentinelsLogoWhite;
      // Prevent infinite loop if the fallback itself fails. `src` reads back
      // as an absolute URL; the attribute keeps the bundled path as set.
      if (img.getAttribute("src") !== fallback) {
        img.src = fallback;
      }
    },
    [rail],
  );

  return (
    // Hints wait longer than the peek, so hovering the rail opens it instead
    // of flashing a tooltip first; keyboard focus still shows them at once.
    <TooltipProvider delayDuration={400}>
      {/* The slot holds the sidebar's place in the layout; while peeking, the
          sidebar is wider than its slot and floats over the page. */}
      <div
        className={cn("relative h-full shrink-0", collapsed ? "w-16" : "w-64")}
        style={{ transition: "width 200ms ease" }}
        data-testid="shell-sidebar-slot"
      >
        <aside
          id={SHELL_SIDEBAR_ID}
          aria-label={t("common.mainNavigation")}
          data-peeking={isPeeking || undefined}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onKeyDown={(e) => {
            if (e.key === "Escape" && isPeeking) setPeeking(false);
          }}
          className={cn(
            "absolute inset-y-0 left-0",
            rail ? "w-16" : "w-64",
            isPeeking && "z-40 shadow-2xl shadow-black/40",
          )}
          style={{
            background: "var(--theme-sidebar-bg)",
            /* WP 1.3: Smooth 200ms transition on org switch.
             WP 1.4: Instant (0ms) when switching to admin/Core shell. */
            transition:
              activeShell === "admin"
                ? "width 200ms ease, background-color 0ms"
                : "width 200ms ease, background-color 200ms ease, color 200ms ease",
          }}
        >
          <div className="flex h-full flex-col overflow-hidden">
            <div
              className="flex min-h-0 flex-1 flex-col"
              onPointerEnter={handleNavPointerEnter}
            >
              {/* Brand header — Sentinels + Client logos; the symbol alone when collapsed */}
              <div
                className={cn(
                  "mb-3.5 flex flex-col items-center gap-2",
                  rail ? "px-2 py-4" : "px-5 py-5",
                )}
                style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
              >
                {rail ? (
                  <img
                    src={sidebarMark}
                    alt={appTitle}
                    className="h-8 w-8 object-contain"
                    onError={handleLogoError}
                    data-testid="shell-sidebar-mark"
                  />
                ) : (
                  <>
                    <img
                      src={sidebarLogo}
                      alt={appTitle}
                      className="h-auto w-full max-w-[195px]"
                      onError={handleLogoError}
                    />
                    {clientLogoSrc && (
                      <img
                        src={clientLogoSrc}
                        alt={t("common.client")}
                        className="h-auto w-full max-w-[160px] brightness-0 invert"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </>
                )}
              </div>

              {/* Main nav */}
              <MainNav
                items={main}
                collapsed={rail}
                activeShell={activeShell}
              />

              {bottom.length > 0 && (
                <>
                  <Separator className="border-white/10" />
                  <nav className="space-y-1 px-2 py-4">
                    {bottom.map((item) => (
                      <SidebarNavLink
                        key={item.href}
                        item={item}
                        collapsed={rail}
                      />
                    ))}
                  </nav>
                </>
              )}
            </div>

            {/* ── Footer: Client selector + Shell switcher + Version & collapse ── */}
            <div onPointerEnter={handleFooterPointerEnter}>
              {hasClientSelector && (
                <div
                  className={cn("py-3", rail ? "px-2" : "px-4")}
                  style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <OrgSwitcher
                    collapsed={rail}
                    onOpenChange={handleMenuOpenChange}
                  />
                </div>
              )}

              {/* Shell switcher — footer */}
              {showSwitcher && (
                <div
                  className={cn("py-2", rail ? "px-2" : "px-4")}
                  style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <ShellSwitcher
                    activeShell={activeShell}
                    availableShells={availableShells}
                    onSwitch={handleShellSwitch}
                    onOpenChange={handleMenuOpenChange}
                    collapsed={rail}
                    t={t}
                  />
                </div>
              )}

              {/* Version + build badge (#373), with the collapse toggle beside it
              (above it on the narrow rail) — clear of the brand header. */}
              <div
                className={cn(
                  "flex items-center",
                  rail
                    ? "flex-col-reverse gap-1 px-2"
                    : "justify-between gap-2 pl-6",
                  onToggleCollapsed ? "py-2" : "py-3",
                  onToggleCollapsed && !rail && "pr-3",
                )}
                style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
              >
                <span
                  className="font-mono text-[10.5px] whitespace-nowrap text-white/40"
                  title={buildTitle}
                  data-testid="shell-build-badge"
                >
                  {rail ? __APP_VERSION__ : buildLabel}
                </span>
                {/* Its state is the layout's, not the peek's: while peeking it
                still reads "expand", and pins the sidebar open. */}
                {onToggleCollapsed && (
                  <SidebarCollapseToggle
                    collapsed={collapsed}
                    onToggle={onToggleCollapsed}
                  />
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </TooltipProvider>
  );
}

/** Collapse/expand toggle in the sidebar footer, next to the build badge. */
function SidebarCollapseToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
}) {
  const { t } = useI18n();
  // Disclosure pattern: the name stays put and `aria-expanded` carries the
  // state; the hint spells out the action for sighted users.
  const action = t(
    collapsed ? "common.expandSidebar" : "common.collapseSidebar",
  );
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;

  return (
    <RailTooltip
      enabled
      content={
        <>
          <span>{action}</span>
          <kbd className="rounded border border-current/30 px-1 font-mono text-[10px] opacity-80">
            {SIDEBAR_SHORTCUT_LABEL}
          </kbd>
        </>
      }
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={t("common.mainNavigation")}
        aria-expanded={!collapsed}
        aria-controls={SHELL_SIDEBAR_ID}
        aria-keyshortcuts={SIDEBAR_SHORTCUT_ARIA}
        data-testid="shell-sidebar-toggle"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/60 transition-colors hover:bg-white/[0.08] hover:text-white focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none"
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
      </button>
    </RailTooltip>
  );
}

function navLabel(item: NavItem, t: (key: TranslationKey) => string): string {
  return item.labelKey ? t(item.labelKey) : item.name;
}

/**
 * Maturity marker (#379, #460) — tells the operator what a surface is before
 * they click it. Legacy entries never reach the sidebar (useShellNav filters
 * them out), so only the two statuses that stay on the menu render a marker:
 * "beta" (works, unfinished) and "planned" (route resolves, screen not built).
 */
const NAV_STATUS_MARKERS = {
  beta: {
    label: "nav.status.beta",
    aria: "nav.status.betaAria",
    testId: "nav-status-beta",
  },
  planned: {
    label: "nav.status.planned",
    aria: "nav.status.plannedAria",
    testId: "nav-status-planned",
  },
} as const satisfies Partial<
  Record<
    NavItemStatus,
    { label: TranslationKey; aria: TranslationKey; testId: string }
  >
>;

function navStatusMarker(status?: NavItemStatus) {
  return status && status in NAV_STATUS_MARKERS
    ? NAV_STATUS_MARKERS[status as keyof typeof NAV_STATUS_MARKERS]
    : undefined;
}

function NavStatusBadge({ status }: { status?: NavItemStatus }) {
  const { t } = useI18n();

  const marker = navStatusMarker(status);

  if (!marker) return null;

  return (
    <span
      className="ml-auto shrink-0 rounded-full border border-white/20 bg-white/10 px-1.5 py-px text-[9.5px] font-semibold tracking-wide text-white/70 uppercase"
      aria-label={t(marker.aria)}
      data-testid={marker.testId}
    >
      {t(marker.label)}
    </span>
  );
}

/**
 * Top-level sidebar entry. Collapsed, the label leaves the row: the icon
 * stays, the link keeps its accessible name and a tooltip names the area
 * (with its maturity marker) on hover and keyboard focus.
 */
function SidebarNavLink({
  item,
  collapsed,
  end,
  sectionActive = false,
}: {
  item: NavItem;
  collapsed: boolean;
  end?: boolean;
  /** Highlight even when not an exact match (a child route is open). */
  sectionActive?: boolean;
}) {
  const { t } = useI18n();
  const label = navLabel(item, t);
  const marker = navStatusMarker(item.status);

  // Collapsed, the visible label and badge are gone: the link names itself,
  // maturity included, as the expanded row's text and badge would.
  const collapsedName = marker ? `${label}, ${t(marker.aria)}` : label;

  const link = (
    <NavLink
      to={item.href}
      end={end}
      aria-label={collapsed ? collapsedName : undefined}
      className={({ isActive }) =>
        cn(
          "relative mx-0 flex items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium transition-all duration-150 focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none",
          collapsed && "justify-center px-2",
          isActive || sectionActive
            ? "bg-white/[0.12] font-semibold text-white"
            : "text-white/[0.72] hover:bg-white/[0.06] hover:text-white",
        )
      }
    >
      {({ isActive }) => (
        <>
          {(isActive || sectionActive) && (
            <span
              className="absolute top-1/2 left-0 h-4 w-[3px] -translate-y-1/2 rounded-r-sm"
              style={{
                background: "var(--theme-sidebar-active-indicator)",
              }}
            />
          )}
          <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {/* Truncates instead of wrapping while the rail widens. */}
          {!collapsed && <span className="min-w-0 truncate">{label}</span>}
          {!collapsed && <NavStatusBadge status={item.status} />}
        </>
      )}
    </NavLink>
  );

  // The trigger wraps the link instead of slotting into it: Radix Slot would
  // stringify NavLink's function className.
  return (
    <RailTooltip
      enabled={collapsed}
      content={
        <>
          <span>{label}</span>
          {marker && (
            <span className="text-[10px] font-semibold tracking-wide uppercase opacity-70">
              {t(marker.label)}
            </span>
          )}
        </>
      }
    >
      <div>{link}</div>
    </RailTooltip>
  );
}

function MainNav({
  items,
  collapsed,
  activeShell,
}: {
  items: NavItem[];
  collapsed: boolean;
  activeShell: string;
}) {
  const location = useLocation();
  const { t } = useI18n();
  const activeProjectId =
    activeShell === "ops" ? getOpsProjectId(location.pathname) : null;
  const { data: projectSummaryEnvelope } = useProjectWorkspaceSummary(
    activeProjectId ?? undefined,
  );
  const projectSummary = projectSummaryEnvelope?.data;

  return (
    <nav className="flex-1 space-y-1 overflow-x-hidden overflow-y-auto px-2 py-4">
      {items.map((item) => {
        // A parent stays highlighted while one of its children is open,
        // collapsed or not; its sub-menu only unfolds in the full sidebar.
        const sectionActive = Boolean(
          item.matchPrefix &&
          item.children &&
          location.pathname.startsWith(item.matchPrefix),
        );
        const showChildren = !collapsed && sectionActive;

        // Detect whether children use absolute hrefs (start with "/") or relative paths
        const firstChild = item.children?.[0];
        const childrenAreAbsolute =
          item.children != null &&
          item.children.length > 0 &&
          firstChild != null &&
          firstChild.href.startsWith("/");

        // For relative children (e.g. ops projects), extract dynamic segment
        const segments = location.pathname.split("/");
        const projectId =
          showChildren && !childrenAreAbsolute && segments.length > 3
            ? segments[3]
            : null;

        return (
          <div key={item.href}>
            <SidebarNavLink
              item={item}
              collapsed={collapsed}
              end={!!item.children}
              sectionActive={sectionActive}
            />

            {/* Absolute-href children (e.g. catalog sub-sections) */}
            {showChildren && childrenAreAbsolute && (
              <div className="mt-1 ml-6 space-y-0.5 border-l-2 border-white/[0.15] pl-2">
                {item.children!.map((child) => {
                  const isChildActive = location.pathname.startsWith(
                    child.href,
                  );

                  return (
                    <NavLink
                      key={child.href}
                      to={child.href}
                      className={cn(
                        "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none",
                        isChildActive
                          ? "bg-white/[0.12] text-white"
                          : "text-white/[0.55] hover:bg-white/[0.06] hover:text-white",
                      )}
                    >
                      <child.icon className="h-3.5 w-3.5 shrink-0" />
                      {navLabel(child, t)}
                      <NavStatusBadge status={child.status} />
                    </NavLink>
                  );
                })}
              </div>
            )}

            {/* Relative-href children with dynamic segment (e.g. ops/projects/:id/...) */}
            {showChildren && !childrenAreAbsolute && projectId && (
              <div className="mt-1 ml-6 space-y-0.5 border-l-2 border-white/[0.15] pl-2">
                <span className="px-2 py-1 font-mono text-[10px] font-semibold tracking-wider text-white/40 uppercase">
                  {t("common.project")}
                </span>
                {item.children!.map((child, index, siblings) => {
                  const childHref = `/ops/projects/${projectId}/${child.href}`;
                  // A heading where a group starts; the stages are numbered.
                  const heading =
                    child.group && child.group !== siblings[index - 1]?.group
                      ? t(PROJECT_NAV_GROUP_LABEL_KEYS[child.group])
                      : null;
                  const stageNumber =
                    child.group === "stages"
                      ? siblings
                          .slice(0, index + 1)
                          .filter((sibling) => sibling.group === "stages")
                          .length
                      : null;
                  const label = (
                    <>
                      {stageNumber !== null ? (
                        <span className="w-3.5 shrink-0 text-center text-[10px] text-white/40 tabular-nums">
                          {stageNumber}
                        </span>
                      ) : (
                        <child.icon className="h-3.5 w-3.5 shrink-0" />
                      )}
                      <span className="min-w-0 flex-1 truncate">
                        {navLabel(child, t)}
                      </span>
                    </>
                  );
                  const headingNode = heading && (
                    <span className="mt-2 block px-2 pt-1 text-[10px] font-semibold tracking-wider text-white/40 uppercase">
                      {heading}
                    </span>
                  );
                  // A stage still to be built: in its place, with no link.
                  if (child.status === "planned") {
                    return (
                      <div key={child.href}>
                        {headingNode}
                        <span
                          className="flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-white/30"
                          aria-disabled="true"
                        >
                          {label}
                          <span className="ml-auto shrink-0 text-[10px] text-white/40">
                            {t("nav.status.soon")}
                          </span>
                        </span>
                      </div>
                    );
                  }
                  const isChildActive =
                    location.pathname === childHref ||
                    location.pathname.startsWith(childHref + "/");
                  const lifecycleStatus =
                    activeShell === "ops"
                      ? getProjectNavLifecycleStatus(
                          child.href,
                          projectSummary?.current_gate ??
                            projectSummary?.wizard_gate,
                        )
                      : null;
                  const lifecycleLabel = lifecycleStatus
                    ? t(
                        PROJECT_NAV_LIFECYCLE_STATUS_LABEL_KEYS[
                          lifecycleStatus
                        ],
                      )
                    : null;

                  return (
                    <div key={child.href}>
                      {headingNode}
                      <NavLink
                        to={childHref}
                        className={cn(
                          "flex items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none",
                          isChildActive
                            ? "bg-white/[0.12] text-white"
                            : cn(
                                "text-white/[0.55] hover:bg-white/[0.06] hover:text-white",
                                lifecycleStatus === "future" && "text-white/40",
                              ),
                        )}
                      >
                        {label}
                        {lifecycleStatus && lifecycleLabel && (
                          <ProjectNavStatusMarker
                            status={lifecycleStatus}
                            label={lifecycleLabel}
                          />
                        )}
                      </NavLink>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

const PROJECT_NAV_GROUP_LABEL_KEYS = {
  stages: "nav.group.stages",
  tools: "nav.group.tools",
} as const satisfies Record<NavItemGroup, TranslationKey>;

function getOpsProjectId(pathname: string): string | null {
  const match = pathname.match(/^\/ops\/projects\/([^/]+)/);
  return match?.[1] ?? null;
}

function ProjectNavStatusMarker({
  status,
  label,
}: {
  status: ProjectNavLifecycleStatus;
  label: string;
}) {
  if (status === "completed") {
    return (
      <span
        className="ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-200"
        title={label}
        aria-label={label}
      >
        <Check className="h-3 w-3" aria-hidden="true" />
      </span>
    );
  }

  if (status === "current") {
    return (
      <span
        className="ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center"
        title={label}
        aria-label={label}
      >
        <span
          className="h-2 w-2 rounded-full ring-2 ring-white/20"
          style={{ background: "var(--theme-sidebar-active-indicator)" }}
          aria-hidden="true"
        />
      </span>
    );
  }

  return (
    <span
      className="ml-auto inline-flex h-4 w-4 shrink-0 items-center justify-center text-white/35"
      title={label}
      aria-label={label}
    >
      <Circle className="h-2.5 w-2.5" aria-hidden="true" />
    </span>
  );
}

function ShellSwitcher({
  activeShell,
  availableShells,
  onSwitch,
  onOpenChange,
  collapsed,
  t,
}: {
  activeShell: string;
  availableShells: string[];
  onSwitch: (shell: ShellId) => void;
  onOpenChange?: (open: boolean) => void;
  collapsed: boolean;
  t: (key: TranslationKey) => string;
}) {
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const activeLabel = t(`shell.${activeShell}.label` as TranslationKey);
  const switcherLabel = `${t("common.shell")}: ${activeLabel}`;

  // One trigger for both states, so toggling the rail keeps its focus.
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <RailTooltip enabled={collapsed} menuOpen={open} content={switcherLabel}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={collapsed ? switcherLabel : undefined}
            className={cn(
              "flex w-full items-center text-white/70 transition-colors hover:text-white focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:outline-none",
              collapsed
                ? "justify-center rounded-md py-2 hover:bg-white/[0.06]"
                : "gap-1 rounded-sm font-mono text-[11px] tracking-widest whitespace-nowrap uppercase",
            )}
          >
            {collapsed ? (
              <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
            ) : (
              <>
                {t("common.shell")}: {activeLabel}
                <ChevronDown className="ml-auto h-3 w-3" />
              </>
            )}
          </button>
        </DropdownMenuTrigger>
      </RailTooltip>
      <DropdownMenuContent
        side={collapsed ? "right" : "bottom"}
        align={collapsed ? "end" : "start"}
        className="w-48"
      >
        {availableShells.map((shell) => {
          const label = t(`shell.${shell}.label` as TranslationKey);
          const description = t(`shell.${shell}.description` as TranslationKey);
          return (
            <DropdownMenuItem
              key={shell}
              onClick={() => {
                onSwitch(shell as ShellId);
                setOpen(false);
              }}
              className={cn(shell === activeShell && "bg-accent")}
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium">{label}</span>
                <span className="text-muted-foreground text-xs">
                  {description}
                </span>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
